import React from 'react';
import {
  Button,
  Result,
  Row,
  Col,
  Typography,
  Card,
  Space,
  Divider,
} from 'antd';
import {
  ReloadOutlined,
  HomeOutlined,
  BugOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { APP_NAME } from 'configs/AppConfig';
import {
  reportError,
  shouldShowErrorDetails,
} from 'configs/ErrorBoundaryConfig';
import { useSelector } from 'react-redux';
import Flex from 'components/shared-components/Flex';

const { Text, Paragraph } = Typography;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error using the centralized error reporting
    reportError(error, errorInfo, {
      component: 'ErrorBoundary',
      timestamp: new Date().toISOString(),
    });

    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
  }

  handleReload = () => {
    // Reset the error boundary
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });

    // Reload the page
    window.location.reload();
  };

  handleGoHome = () => {
    // Reset the error boundary
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });

    // Navigate to home
    window.location.href = '/app/dashboard';
  };

  toggleDetails = () => {
    this.setState((prevState) => ({
      showDetails: !prevState.showDetails,
    }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallbackUI
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          showDetails={this.state.showDetails}
          onReload={this.handleReload}
          onGoHome={this.handleGoHome}
          onToggleDetails={this.toggleDetails}
          fallbackComponent={this.props.fallbackComponent}
        />
      );
    }

    return this.props.children;
  }
}

const ErrorFallbackUI = ({
  error,
  errorInfo,
  showDetails,
  onReload,
  onGoHome,
  onToggleDetails,
  fallbackComponent,
}) => {
  const theme = useSelector((state) => state.theme.currentTheme);

  // If a custom fallback component is provided, use it
  if (fallbackComponent) {
    return fallbackComponent({ error, errorInfo, onReload, onGoHome });
  }

  const isDark = theme === 'dark';

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="container-fluid d-flex flex-column justify-content-between min-h-screen px-md-4 pb-md-4 pt-md-4">
        {/* Header */}
        <div className="mb-4">
          <img
            className="img-fluid"
            src={`/img/${isDark ? 'logo-white.png' : 'logo.png'}`}
            alt={APP_NAME}
            style={{ maxHeight: '60px' }}
          />
        </div>

        {/* Main Content */}
        <div className="flex-grow-1 d-flex align-items-center">
          <div className="container">
            <Row justify="center">
              <Col xs={24} sm={20} md={16} lg={12} xl={10}>
                <Card
                  className={`shadow-lg ${isDark ? 'border-gray-700' : ''}`}
                  bodyStyle={{ padding: '40px' }}
                >
                  <Result
                    status="error"
                    icon={
                      <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                    }
                    title={
                      <Typography.Title level={3} className="mb-3">
                        Oops! Something went wrong
                      </Typography.Title>
                    }
                    subTitle={
                      <div>
                        <Paragraph className="mb-4 text-muted">
                          We encountered an unexpected error while loading this
                          page. Don't worry, our team has been notified and is
                          working to fix this issue.
                        </Paragraph>

                        <Space
                          direction="vertical"
                          size="middle"
                          className="w-100"
                        >
                          <Space wrap className="w-100 justify-content-center">
                            <Button
                              type="primary"
                              icon={<ReloadOutlined />}
                              onClick={onReload}
                              size="large"
                            >
                              Try Again
                            </Button>
                            <Button
                              icon={<HomeOutlined />}
                              onClick={onGoHome}
                              size="large"
                            >
                              Go to Dashboard
                            </Button>
                          </Space>

                          {shouldShowErrorDetails() && (
                            <>
                              <Divider />
                              <div className="text-center">
                                <Button
                                  type="link"
                                  icon={<BugOutlined />}
                                  onClick={onToggleDetails}
                                  size="small"
                                >
                                  {showDetails ? 'Hide' : 'Show'} Error Details
                                </Button>
                              </div>
                            </>
                          )}
                        </Space>
                      </div>
                    }
                  />

                  {/* Error Details (Development Only) */}
                  {shouldShowErrorDetails() && showDetails && (
                    <Card
                      size="small"
                      title="Error Details (Development Only)"
                      className="mt-3"
                      style={{
                        backgroundColor: isDark ? '#1a1a1a' : '#f9f9f9',
                      }}
                    >
                      <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                        {error && (
                          <div className="mb-3">
                            <Text strong>Error Message:</Text>
                            <pre
                              style={{
                                fontSize: '12px',
                                marginTop: '8px',
                                padding: '8px',
                                backgroundColor: isDark ? '#2a2a2a' : '#fff',
                                border: `1px solid ${isDark ? '#444' : '#d9d9d9'}`,
                                borderRadius: '4px',
                                overflow: 'auto',
                              }}
                            >
                              {error.toString()}
                            </pre>
                          </div>
                        )}

                        {errorInfo && errorInfo.componentStack && (
                          <div>
                            <Text strong>Component Stack:</Text>
                            <pre
                              style={{
                                fontSize: '11px',
                                marginTop: '8px',
                                padding: '8px',
                                backgroundColor: isDark ? '#2a2a2a' : '#fff',
                                border: `1px solid ${isDark ? '#444' : '#d9d9d9'}`,
                                borderRadius: '4px',
                                overflow: 'auto',
                              }}
                            >
                              {errorInfo.componentStack}
                            </pre>
                          </div>
                        )}
                      </div>
                    </Card>
                  )}
                </Card>
              </Col>
            </Row>
          </div>
        </div>

        {/* Footer */}
        <Flex mobileFlex={false} justifyContent="between" className="mt-4">
          <Text type="secondary">
            Copyright © {new Date().getFullYear()}{' '}
            <Text strong>{APP_NAME}</Text>
          </Text>
          <div>
            <Text type="secondary" className="text-muted">
              Need help? Contact our support team
            </Text>
          </div>
        </Flex>
      </div>
    </div>
  );
};

export default ErrorBoundary;
